import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { MoveDiagonalIcon, PlusIcon } from 'lucide-react';
import { Link } from 'react-router';

import Header from '../Header';
import { RightSidebarWrapper } from '../RightSidebar/RightSidebarWrapper';
import useOutletContext from '../useOutletContext';

import {
  formatAllocationType,
  formatCurrency,
  formatDate,
  formatStatus,
  GET_ALLOCATION_HISTORY,
  type GetAllocationHistoryResponse,
  getStatusBadgeClass
} from './graphql';
import { RequestAllocationDialog } from './RequestAllocationDialog';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Empty, EmptyContent } from '@/components/ui/empty';
import { Spinner } from '@/components/ui/spinner';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';

interface GetDealOpportunity {
  node: {
    __typename: 'OpportunityNode';
    id: string;
    smProjectedDebtInvestment: null | number;
    smProjectedEquityInvestment: null | number;
    targetEntityId: {
      __typename: 'AccountNode';
      id: string;
      opstreamAssets:
        | {
            id: string;
            previouslyParticipatingFundsIds:
              | {
                  id: string;
                  includeIneligibleFundsInAllocation: boolean | null;
                  name: null | string;
                }[]
              | null;
          }[]
        | null;
    } | null;
  };
}

const GET_DEAL_OPPORTUNITY = gql`
  query DealOpportunity($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        smProjectedDebtInvestment
        smProjectedEquityInvestment
        targetEntityId {
          ... on AccountNode {
            id
            opstreamAssets {
              id
              previouslyParticipatingFundsIds {
                id
              }
            }
          }
        }
      }
    }
  }
`;

const InvestmentsDirectIdAllocations = () => {
  const { id, loading, projectName } = useOutletContext();

  // Decode the base64 ID to get the raw integer ID for the backend
  const decodedId = id ? atob(id).split(':')[1] : null;

  const { data: data1, loading: loading1 } = useQuery<
    GetAllocationHistoryResponse,
    { opportunityId: string }
  >(GET_ALLOCATION_HISTORY, {
    skip: !decodedId,
    variables: { opportunityId: decodedId! }
  });

  const { data: data2, loading: loading2 } = useQuery<
    GetDealOpportunity,
    { id: string }
  >(GET_DEAL_OPPORTUNITY, {
    variables: { id }
  });

  if (loading || loading1 || loading2) {
    return (
      <div className="-mt-16 grid h-[calc(100%+64px)] place-content-center">
        <Spinner className="size-8 text-blue-500" />
      </div>
    );
  }

  if (!data1 || !data2) {
    return <div>{'Error'}</div>;
  }

  const allocations = data1.allocationHistory;

  return (
    <>
      <Header />
      <RightSidebarWrapper>
        <div className="min-w-0 space-y-2.5 px-5 pt-5 pb-16.5 md:pl-14">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Allocation History</h2>
            {allocations.length !== 0 && (
              <RequestAllocationDialog
                dealId={id}
                dealName={projectName}
                isExistingDeal={true}
                smProjectedDebtInvestment={data2.node.smProjectedDebtInvestment}
                smProjectedEquityInvestment={
                  data2.node.smProjectedEquityInvestment
                }
                targetEntityId={data2.node.targetEntityId}
              >
                <Button>
                  <PlusIcon />
                  Request Allocation
                </Button>
              </RequestAllocationDialog>
            )}
          </div>
          <div className="bg-card rounded-md border p-5">
            {allocations.length === 0 ? (
              <Empty className="p-0!">
                <EmptyContent>
                  <RequestAllocationDialog
                    dealId={id}
                    dealName={projectName}
                    isExistingDeal={true}
                    smProjectedDebtInvestment={
                      data2.node.smProjectedDebtInvestment
                    }
                    smProjectedEquityInvestment={
                      data2.node.smProjectedEquityInvestment
                    }
                    targetEntityId={data2.node.targetEntityId}
                  >
                    <Button>
                      <PlusIcon />
                      Request Allocation
                    </Button>
                  </RequestAllocationDialog>
                </EmptyContent>
              </Empty>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-0" />
                    <TableHead className="text-xs font-semibold">
                      Allocation Amount
                    </TableHead>
                    <TableHead className="w-[68px] text-xs font-semibold">
                      Version
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Allocation Type
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Status
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Created Date
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Total Allocated
                    </TableHead>
                    <TableHead className="text-xs font-semibold">
                      Number of Funds
                    </TableHead>
                    <TableHead className="w-[80px] text-xs leading-4 font-semibold whitespace-normal">
                      Has Warnings
                    </TableHead>
                    <TableHead className="w-[80px] text-xs leading-4 font-semibold whitespace-normal">
                      Has Overrides
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.map(allocation => (
                    <TableRow key={allocation.id}>
                      <TableCell className="w-0">
                        <Button
                          asChild
                          size="icon"
                          variant="ghost"
                        >
                          <Link to={allocation.id}>
                            <MoveDiagonalIcon />
                          </Link>
                        </Button>
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(allocation.allocationAmount)}
                      </TableCell>
                      <TableCell className="text-right">
                        {allocation.version ?? '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="pastel-slate">
                          {formatAllocationType(allocation.allocationType)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge
                          className={getStatusBadgeClass(allocation.status)}
                        >
                          {formatStatus(allocation.status)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {formatDate(allocation.createdDate)}
                      </TableCell>
                      <TableCell className="text-right">
                        {formatCurrency(allocation.totalAllocated)}
                      </TableCell>
                      <TableCell className="text-right">
                        {allocation.numberOfFunds ?? '-'}
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={allocation.hasWarnings}
                          disabled
                        />
                      </TableCell>
                      <TableCell className="text-center">
                        <Checkbox
                          checked={allocation.hasOverrides}
                          disabled
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </RightSidebarWrapper>
    </>
  );
};

export default InvestmentsDirectIdAllocations;
